import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { Card } from '@/components/ui/card'
import { gtmEvents, ConsentSettings } from '@/lib/gtm'
import { Cookie, Shield, X } from 'lucide-react'

export const CookieBanner = () => {
  const [isVisible, setIsVisible] = useState(false)
  const [showPreferences, setShowPreferences] = useState(false)

  // Preferences state
  const [preferences, setPreferences] = useState({
    analytics: true,
    marketing: true,
  })

  useEffect(() => {
    const savedConsent = localStorage.getItem('dp6-cookie-consent')
    if (savedConsent) {
      try {
        const parsed = JSON.parse(savedConsent)
        // Ensure consent is updated on load if it exists
        const consentSettings: ConsentSettings = {
          ad_storage: parsed.marketing ? 'granted' : 'denied',
          ad_user_data: parsed.marketing ? 'granted' : 'denied',
          ad_personalization: parsed.marketing ? 'granted' : 'denied',
          analytics_storage: parsed.analytics ? 'granted' : 'denied',
        }
        gtmEvents.updateConsent(consentSettings)
        setIsVisible(false)
      } catch (e) {
        // If error parsing, show banner
        setIsVisible(true)
      }
    } else {
      setIsVisible(true)
    }
  }, [])

  const saveConsent = (analytics: boolean, marketing: boolean) => {
    const consentSettings: ConsentSettings = {
      ad_storage: marketing ? 'granted' : 'denied',
      ad_user_data: marketing ? 'granted' : 'denied',
      ad_personalization: marketing ? 'granted' : 'denied',
      analytics_storage: analytics ? 'granted' : 'denied',
    }

    gtmEvents.updateConsent(consentSettings)
    localStorage.setItem(
      'dp6-cookie-consent',
      JSON.stringify({ analytics, marketing }),
    )
    setIsVisible(false)
  }

  const handleAcceptAll = () => saveConsent(true, true)
  const handleRejectAll = () => saveConsent(false, false)
  const handleSavePreferences = () =>
    saveConsent(preferences.analytics, preferences.marketing)

  if (!isVisible) return null

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6 bg-background/80 backdrop-blur-sm animate-in slide-in-from-bottom duration-500">
      <Card className="container mx-auto max-w-4xl shadow-2xl border-primary/20">
        {!showPreferences ? (
          <div className="flex flex-col md:flex-row gap-6 p-6 items-center md:items-start">
            <div className="flex-1 space-y-3 text-center md:text-left">
              <div className="flex items-center justify-center md:justify-start gap-2">
                <Cookie className="h-6 w-6 text-primary" />
                <h3 className="text-lg font-semibold">
                  Sua privacidade é importante
                </h3>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Nós utilizamos cookies para melhorar sua experiência de
                navegação, analisar o tráfego do site e personalizar o conteúdo.
                Ao clicar em "Aceitar Todos", você concorda com o uso de cookies
                para essas finalidades. Você pode gerenciar suas preferências a
                qualquer momento.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto min-w-[200px]">
              <Button
                variant="default"
                className="flex-1"
                onClick={handleAcceptAll}
              >
                Aceitar Todos
              </Button>
              <Button
                variant="outline"
                className="flex-1"
                onClick={handleRejectAll}
              >
                Rejeitar
              </Button>
              <Button
                variant="ghost"
                className="flex-1"
                onClick={() => setShowPreferences(true)}
              >
                Personalizar
              </Button>
            </div>
          </div>
        ) : (
          <div className="p-6">
            <div className="flex items-center justify-between mb-6 border-b pb-4">
              <div className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-primary" />
                <h3 className="text-lg font-semibold">
                  Preferências de Cookies
                </h3>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowPreferences(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            <div className="grid gap-6 mb-8">
              <div className="flex items-start space-x-4">
                <Switch id="necessary" checked disabled />
                <div className="space-y-1">
                  <Label
                    htmlFor="necessary"
                    className="font-medium text-base cursor-default opacity-70"
                  >
                    Essenciais (Obrigatório)
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Necessários para o funcionamento básico do site, como manter
                    o carrinho de compras e navegação segura.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <Switch
                  id="analytics"
                  checked={preferences.analytics}
                  onCheckedChange={(checked) =>
                    setPreferences((prev) => ({ ...prev, analytics: checked }))
                  }
                />
                <div className="space-y-1">
                  <Label
                    htmlFor="analytics"
                    className="font-medium text-base cursor-pointer"
                  >
                    Analíticos
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Nos ajudam a entender como os visitantes interagem com o
                    site, coletando informações de forma anônima.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <Switch
                  id="marketing"
                  checked={preferences.marketing}
                  onCheckedChange={(checked) =>
                    setPreferences((prev) => ({ ...prev, marketing: checked }))
                  }
                />
                <div className="space-y-1">
                  <Label
                    htmlFor="marketing"
                    className="font-medium text-base cursor-pointer"
                  >
                    Marketing
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Utilizados para exibir anúncios relevantes e medir a
                    eficácia das campanhas publicitárias.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => setShowPreferences(false)}
              >
                Voltar
              </Button>
              <Button onClick={handleSavePreferences}>
                Salvar Preferências
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  )
}
